import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api, type Health as H } from "../lib/api";

function ago(iso?: string | null) {
  if (!iso) return "never";
  return fmt.when(iso);
}

function Row({ label, value, bad }: { label: string; value: React.ReactNode; bad?: boolean }) {
  return (
    <div className="brk" style={{ fontFamily: "var(--sans)", padding: "6px 0" }}>
      <span className="dim">{label}</span>
      <span className="mono" style={{ color: bad ? "var(--bad)" : undefined }}>
        {value}
      </span>
    </div>
  );
}

const list = (m: Record<string, number>) =>
  Object.entries(m)
    .map(([k, v]) => `${fmt.n(v)} ${k.replace(/_/g, " ")}`)
    .join(" · ") || "none";

/** Everything that runs in the background, on one screen. The same numbers
 *  the Slack alerts read, so the screen and the alerts cannot disagree. */
export function Health() {
  const [h, setH] = useState<H | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = () =>
      api
        .health()
        .then((r) => {
          setH(r);
          setError(null);
        })
        .catch((e) => setError(e.message));
    load();
    // the server caches for a minute, so polling faster would only re-read it
    const t = setInterval(() => !document.hidden && load(), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!h) return <>{error ? <ErrorBanner error={error} /> : <div className="empty">Loading…</div>}</>;
  const s = h.sending;
  const w = h.webhooks;
  return (
    <>
      <ErrorBanner error={error} />
      <div className="row w">
        <h2 style={{ fontSize: 19 }}>System health</h2>
        <div className="sp" />
        <span className="dim" style={{ fontSize: 12 }}>
          as of {fmt.et(h.at)} · refreshes every minute
        </span>
      </div>

      {h.alerts.length === 0 ? (
        <div className="banner ok stack" style={{ margin: "10px 0" }}>
          Nothing needs attention right now.
        </div>
      ) : (
        h.alerts.map((a) => (
          <div key={a.key} className={`banner ${a.severity === "critical" ? "bad" : "warn"}`} style={{ margin: "8px 0" }}>
            {a.text}
          </div>
        ))
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14 }}>
        <Card
          title={
            <div className="row" style={{ gap: 8 }}>
              <h3>Sending</h3>
              {s.paused ? <Pill tone="ba">paused</Pill> : s.in_sending_hours ? <Pill tone="ok">in hours</Pill> : <Pill tone="nu">outside hours</Pill>}
            </div>
          }
        >
          <Row label="Sent today" value={fmt.n(s.sent_today)} />
          <Row label="In the last hour / 5 minutes" value={`${fmt.n(s.sent_last_hour)} / ${fmt.n(s.sent_last_5_minutes)}`} />
          <Row label="Due now" value={`${fmt.n(s.due_now)} (${fmt.n(s.being_sent)} being sent)`} />
          {/* Its own row, not folded into "due now". A paused campaign's texts
              stay queued, and counting them as due read as a stalled pipeline
              3.8 days behind on 27 Sep 2026 while nothing was wrong. Still
              shown, because 6,000 texts waiting on a hold is worth knowing. */}
          {s.due_but_held > 0 && (
            <Row label="Waiting on a paused campaign" value={fmt.n(s.due_but_held)} />
          )}
          <Row label="Oldest due text" value={`${s.oldest_due_minutes_late} min behind`} bad={s.oldest_due_minutes_late > 90} />
          <Row label="Failed today" value={list(s.failures_today)} />
          <Row label="Held back at send today" value={list(s.held_back_today)} />
          {s.paused && <Row label="Paused because" value={s.paused_because ?? "—"} bad />}
        </Card>

        <Card title="Webhooks from GHL">
          <Row label="Replies — last one" value={ago(w.replies_from_ghl.last_at)} />
          <Row label="Replies — last hour / 24h" value={`${fmt.n(w.replies_from_ghl.last_hour)} / ${fmt.n(w.replies_from_ghl.last_24h)}`} />
          <Row label="Appointments — last one" value={ago(w.appointments.last_at)} />
          <Row label="Appointments — 24h / stuck" value={`${fmt.n(w.appointments.last_24h)} / ${fmt.n(w.appointments.stuck)}`} bad={w.appointments.stuck > 0} />
          <Row label="Purchases — last one" value={ago(w.purchases.last_at)} />
          <Row label="Purchases — 24h / stuck" value={`${fmt.n(w.purchases.last_24h)} / ${fmt.n(w.purchases.stuck)}`} bad={w.purchases.stuck > 0} />
        </Card>

        <Card title={<div className="row" style={{ gap: 8 }}><h3>Appointment reminders</h3><Link to="/reminders" style={{ fontSize: 12 }}>open schedule</Link></div>}>
          <Row label="Sent today" value={fmt.n(h.reminders.sent_today)} />
          <Row label="Still to go today" value={fmt.n(h.reminders.still_due_today)} />
          <Row label="Overdue" value={fmt.n(h.reminders.overdue)} bad={h.reminders.overdue > 0} />
          <Row label="Cancelled today" value={list(h.reminders.cancelled_because)} />
          <Row label="Next one" value={h.reminders.next_due_at ? fmt.et(h.reminders.next_due_at) : "—"} />
        </Card>

        <Card title="GHL send tokens">
          <Row label="Authorisation failures — last hour" value={fmt.n(h.tokens.auth_failures_last_hour)} bad={h.tokens.auth_failures_last_hour > 0} />
          <Row label="Authorisation failures — 24h" value={fmt.n(h.tokens.auth_failures_24h)} />
          <Row label="Gateway unavailable — last hour" value={fmt.n(h.tokens.gateway_unavailable_last_hour)} />
          <Row label="Throttled — last hour" value={fmt.n(h.tokens.throttled_last_hour)} />
        </Card>

        <Card title="GHL backfill">
          <Row label="Last run" value={h.backfill.last_run_at ? ago(h.backfill.last_run_at) : "not recorded yet"} />
          <Row label="Batches / contacts, 48h" value={`${fmt.n(h.backfill.calls_48h)} / ${fmt.n(h.backfill.contacts_received_48h)}`} />
          <Row label="Rows that failed, 48h" value={fmt.n(h.backfill.rows_failed_48h)} bad={h.backfill.rows_failed_48h > 0} />
          <Row label="Newest call data" value={h.backfill.newest_call_data ? fmt.et(h.backfill.newest_call_data) : "—"} />
        </Card>

        <Card title="Claude-written messages">
          <Row label="Switched on" value={h.drafting.switched_on ? "yes" : "no"} />
          <Row label="Drafts in the last 24h" value={list(h.drafting.drafts_24h)} />
        </Card>

        <Card title="Database">
          <Row label="Open connections" value={`${fmt.n(h.database.connections)} of ${fmt.n(h.database.max_connections)}`} bad={h.database.connections >= 120} />
          <Row label="Size" value={h.database.size} />
        </Card>
      </div>
    </>
  );
}
